"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Container } from "@/components/ui/container";
import { Eyebrow } from "@/components/ui/eyebrow";
import { ScrollReanimateHeader } from "@/components/motion/scroll-reanimate-header";
import { TextRevealClip } from "@/components/motion/text-reveal-clip";
import type { Dictionary } from "@/i18n/dictionaries";

export function FaqCompact({ dict }: { dict: Dictionary }) {
  // First item open by default to match prior behavior.
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 sm:py-28">
      <Container>
        <div className="mx-auto max-w-3xl">
          <ScrollReanimateHeader className="text-center">
            <Eyebrow tone="muted">{dict.faq.intro.eyebrow}</Eyebrow>
            <h2 className="mt-4 text-[34px] font-semibold leading-[1.08] tracking-[-0.04em] text-neutral-950 sm:text-[44px]">
              <TextRevealClip>{dict.faq.intro.heading}</TextRevealClip>
            </h2>
          </ScrollReanimateHeader>

          <div className="mt-12 divide-y divide-neutral-200 rounded-2xl border border-neutral-200 bg-white">
            {dict.faq.items.map((item, i) => {
              const isOpen = openIndex === i;
              const contentId = `faq-${i}-content`;
              return (
                <div key={item.question} className="px-6 py-5">
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    className="flex w-full cursor-pointer list-none items-center justify-between gap-4 text-left"
                  >
                    <span className="text-[15px] font-semibold tracking-tight text-neutral-950 sm:text-base">
                      {item.question}
                    </span>
                    <motion.span
                      aria-hidden
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ type: "spring", stiffness: 320, damping: 24 }}
                      className="grid size-7 shrink-0 place-items-center rounded-full border border-neutral-200 text-neutral-500"
                    >
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="size-3.5"
                      >
                        <path d="M8 3v10M3 8h10" strokeLinecap="round" />
                      </svg>
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={contentId}
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { type: "spring", stiffness: 240, damping: 28, mass: 0.7 },
                          opacity: { duration: 0.18, ease: "easeOut" },
                        }}
                        className="overflow-hidden"
                      >
                        <p className="mt-3 text-sm leading-relaxed tracking-tight text-neutral-500 sm:text-base">
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}
